import { useSelector, useDispatch } from 'react-redux'
import { useCallback, useEffect, useRef } from 'react'
import type { RootState } from '../store'
import {
  setTheme,
  setColorScheme,
  setFontSize,
  setLanguage,
  resetTheme
} from '../store/slices/themeSlice'
import {
  setSidebarOpen as setSidebarOpenAction,
  toggleSidebarOpen as toggleSidebarOpenAction
} from '../store/slices/uiSlice'
import { setLocalStorage, getLocalStorage } from '../utils/helpers'
import { THEME_MODES, LANGUAGES, STORAGE_KEYS } from '../utils/constants'

interface UseThemeReturn {
  // State
  theme: 'light' | 'dark' | 'system'
  colorScheme: string
  isDark: boolean
  sidebarOpen: boolean

  fontSize: 'small' | 'medium' | 'large'
  language: string
  
  // Actions
  changeTheme: (theme: 'light' | 'dark' | 'system') => void
  changeColorScheme: (scheme: string) => void
  toggleSidebarOpen: () => void
  setSidebarOpen: (open: boolean) => void

  changeFontSize: (size: 'small' | 'medium' | 'large') => void
  changeLanguage: (language: string) => void
  resetToDefaults: () => void
  
  // Utilities
  getThemeClass: () => string
  getFontSizeClass: () => string
  applyTheme: () => void
  detectSystemTheme: () => 'light' | 'dark'
  isSystemDark: () => boolean
}

const useTheme = (): UseThemeReturn => {
  const dispatch = useDispatch()
  const initializedRef = useRef(false)
  
  const {
    theme,
    colorScheme,
    fontSize,
    language
  } = useSelector((state: RootState) => state.theme)
  
  const { sidebarOpen } = useSelector((state: RootState) => state.ui)

  // Detect system theme preference
  const detectSystemTheme = useCallback((): 'light' | 'dark' => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }, [])

  // Check if system prefers dark mode
  const isSystemDark = useCallback((): boolean => {
    return detectSystemTheme() === 'dark'
  }, [detectSystemTheme])

  // Determine if current theme is dark
  const isDark = theme === 'dark' || (theme === 'system' && isSystemDark())

  // Change theme
  const changeTheme = useCallback((newTheme: 'light' | 'dark' | 'system') => {
    dispatch(setTheme(newTheme))
    localStorage.setItem(STORAGE_KEYS.THEME, newTheme)
  }, [dispatch])

  // Change color scheme
  const changeColorScheme = useCallback((scheme: string) => {
    dispatch(setColorScheme(scheme as any))
    localStorage.setItem(STORAGE_KEYS.COLOR_SCHEME, scheme)
  }, [dispatch])

  // Toggle sidebar
  const toggleSidebarOpen = useCallback(() => {
    dispatch(toggleSidebarOpenAction())
    localStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, (!sidebarOpen).toString())
  }, [dispatch, sidebarOpen])

  // Set sidebar open state
  const setSidebarOpen = useCallback((open: boolean) => {
    dispatch(setSidebarOpenAction(open))
    localStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, open.toString())
  }, [dispatch])



  // Change font size
  const changeFontSize = useCallback((size: 'small' | 'medium' | 'large') => {
    dispatch(setFontSize(size))
    localStorage.setItem(STORAGE_KEYS.FONT_SIZE, size)
  }, [dispatch])

  // Change language
  const changeLanguage = useCallback((lang: string) => {
    dispatch(setLanguage(lang as any))
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang)
    
    // Update document language
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang
    }
  }, [dispatch])

  // Reset to default settings
  const resetToDefaults = useCallback(() => {
    dispatch(resetTheme())
    
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEYS.THEME)
    localStorage.removeItem(STORAGE_KEYS.COLOR_SCHEME)
    localStorage.removeItem(STORAGE_KEYS.SIDEBAR_OPEN)
    localStorage.removeItem(STORAGE_KEYS.FONT_SIZE)
    localStorage.removeItem(STORAGE_KEYS.LANGUAGE)
  }, [dispatch])

  // Get theme class for CSS
  const getThemeClass = useCallback((): string => {
    const baseClass = isDark ? 'dark' : 'light'
    return `theme-${baseClass} color-scheme-${colorScheme}`
  }, [isDark, colorScheme])

  // Get font size class
  const getFontSizeClass = useCallback((): string => {
    return `font-size-${fontSize}`
  }, [fontSize])

  // Apply theme to document
  const applyTheme = useCallback(() => {
    if (typeof document === 'undefined') return

    const root = document.documentElement
    const body = document.body

    // Remove existing theme classes
    root.classList.remove('light', 'dark', 'theme-light', 'theme-dark')
    body.classList.remove('light', 'dark', 'theme-light', 'theme-dark')

    // Add current theme classes
    const themeClass = isDark ? 'dark' : 'light'
    root.classList.add(themeClass, `theme-${themeClass}`)
    body.classList.add(themeClass, `theme-${themeClass}`)

    // Set color scheme
    root.setAttribute('data-color-scheme', colorScheme)
    
    // Set font size
    root.setAttribute('data-font-size', fontSize)
    
    // Set language
    root.setAttribute('lang', language)

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#1a1a1a' : '#ffffff')
    }

    // Update CSS custom properties
    root.style.setProperty('--theme-mode', isDark ? 'dark' : 'light')
    root.style.setProperty('--color-scheme', colorScheme)
    root.style.setProperty('--font-size-scale', fontSize === 'small' ? '0.875' : fontSize === 'large' ? '1.125' : '1')
  }, [isDark, colorScheme, fontSize, language])

  // Load saved theme settings on mount
  useEffect(() => {
    if (initializedRef.current) return
    
    const savedTheme = localStorage.getItem(STORAGE_KEYS.THEME) || 'system'
    const savedColorScheme = localStorage.getItem(STORAGE_KEYS.COLOR_SCHEME) || 'blue'
    const savedSidebarOpen = localStorage.getItem(STORAGE_KEYS.SIDEBAR_OPEN) === 'true'
    const savedFontSize = localStorage.getItem(STORAGE_KEYS.FONT_SIZE) || 'medium'
    const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE) || 'tr'

    // Dispatch all saved values
    dispatch(setTheme(savedTheme as any))
    dispatch(setColorScheme(savedColorScheme as any))
    if (savedSidebarOpen !== sidebarOpen) {
      dispatch(toggleSidebarOpenAction())
    }
    dispatch(setFontSize(savedFontSize as any))
    dispatch(setLanguage(savedLanguage as any))
    
    initializedRef.current = true
  }, []) // Empty dependency array - only run on mount

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme()
  }, [isDark, colorScheme, fontSize, language]) // Direct dependencies instead of applyTheme

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = () => {
      applyTheme()
    }

    mediaQuery.addEventListener('change', handleChange)
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme]) // Remove applyTheme dependency

  // Handle window resize for responsive sidebar - disabled on mobile to keep sidebar always visible
  useEffect(() => {
    // No-op: mobilde menü daralma/kapama davranışı kapalı
  }, [])

  return {
  // State
  theme,
  colorScheme,
  isDark,
  sidebarOpen,
  fontSize,
  language,
  
  // Actions
  changeTheme,
  changeColorScheme,
  toggleSidebarOpen,
  setSidebarOpen,
  changeFontSize,
  changeLanguage,
  resetToDefaults,
  
  // Utilities
  getThemeClass,
  getFontSizeClass,
  applyTheme,
  detectSystemTheme,
  isSystemDark
}
}

export default useTheme